import Debug from 'debug';
import {
  CompoundPayload,
  CompoundResponse,
  createData,
  DataRowAction,
  Entity,
  getFieldArray,
  getRowKey,
  getUuid,
  PersistenceType,
  QueryResponse,
  RecordResponse,
  Row,
  RowEnvelope,
  validate,
} from 'gossamer-unified';
import { isNotFound } from '../data/ReduxGCacheMiddleware';
import { notify } from '../ancillary/NotifyManager';

const debug = Debug('gfe/fo/Utils');

// TODO: convert this to RowEnvelope - why have two different data structures doing the same thing?
// because Formik cannot support a nested structure?
export type FormRow<T extends Row<T>> = Partial<T> & {
  _action: DataRowAction;
  _deleting?: boolean;
  _original_key?: string;
  _temp_new_key?: string;
};

export type AllValues = Record<string, FormRow<{}> | FormRow<{}>[] | string>;

export type CreateSingleInitializer<T extends Row<T>> = {
  entity: Entity<T>;
  type: 'create-single';
  override?: Partial<T>;
};
export type UpdateSingleInitializer<T extends Row<T>> = {
  entity: Entity<T>;
  type: 'update-single';
  override?: Partial<T>;
  record: RecordResponse<T>;
  createOnNotFound?: boolean;
};
export type CreateMultiInitializer<T extends Row<T>> = { entity: Entity<T>; type: 'create-multi'; data?: Partial<T>[] };
export type UpdateMultiInitializer<T extends Row<T>> = {
  entity: Entity<T>;
  type: 'update-multi';
  query: QueryResponse<T>;
};

export type Initializer<T extends Row<T>> =
  | CreateSingleInitializer<T>
  | UpdateSingleInitializer<T>
  | CreateMultiInitializer<T>
  | UpdateMultiInitializer<T>;
export type Initialization = Record<string, Initializer<{}>>;

export const convertToFormRowCreate = <T extends Row<T>>(entity: Entity<T>, row: Partial<T>): FormRow<T> => {
  const out: FormRow<T> = {
    _action: 'C',
    _deleting: false,
    ...row,
  };
  try {
    out._original_key = getRowKey(entity, row);
  } catch (e) {
    out._temp_new_key = getUuid();
  }
  return out;
};

export const convertToFormRowUpdate = <T extends Row<T>>(entity: Entity<T>, row: Partial<T>): FormRow<T> => ({
  _action: 'U',
  _deleting: false,
  _original_key: getRowKey(entity, row),
  ...row,
});

export const mapInitializerToResponse = (initializer: Initializer<{}>) =>
  initializer.type === 'update-single'
    ? initializer.record
    : initializer.type === 'update-multi'
      ? initializer.query
      : null;

export const getAllInitialValues = (init: Initialization): AllValues => {
  const initialValues: AllValues = {
    _outcome: 'save',
  };
  Object.keys(init).forEach((valuesKey) => {
    initialValues[valuesKey] = getInitialValues(init[valuesKey]);
  });
  return initialValues;
};

export const getInitialValues = <T extends Row<T>>(initializer: Initializer<T>) =>
  initializer.type === 'create-single'
    ? getInitialValuesNewRow(initializer.entity, initializer.override)
    : initializer.type === 'update-single'
      ? getInitialValuesUpdateSingle(initializer)
      : initializer.type === 'create-multi'
        ? getInitialValuesCreateMulti(initializer)
        : getInitialValuesUpdateMulti(initializer);

export const getInitialValuesUpdateSingle = <T extends Row<T>>(initializer: UpdateSingleInitializer<T>) => {
  if (initializer.record.isFetching) {
    throw new Error(`record response is still fetching`);
  }
  if (initializer.record.error) {
    if (!initializer.createOnNotFound || !isNotFound(initializer.record)) {
      throw new Error(initializer.record.error);
    }
    return getInitialValuesNewRow(initializer.entity, initializer.override);
  } else {
    return getInitialValuesExistingRow(initializer.entity, initializer.record.data, initializer.override);
  }
};

export const getInitialValuesExistingRow = <T extends Row<T>>(entity: Entity<T>, data: T, override?: Partial<T>) => {
  return convertToFormRowUpdate(entity, { ...data, ...override });
};

export const getInitialValuesNewRow = <T extends Row<T>>(entity: Entity<T>, override?: Partial<T>) => {
  return convertToFormRowCreate(entity, { ...createData(entity), ...override });
};

export const getInitialValuesCreateMulti = <T extends Row<T>>(initializer: CreateMultiInitializer<T>) => {
  if (initializer.data) {
    return initializer.data.map((dataRow) => convertToFormRowCreate(initializer.entity, dataRow));
  }
  return [];
};

export const getInitialValuesUpdateMulti = <T extends Row<T>>(initializer: UpdateMultiInitializer<T>) => {
  if (initializer.query.isFetching) {
    throw new Error(`query response is still fetching`);
  }
  if (initializer.query.error) {
    throw new Error(initializer.query.error);
  }
  return initializer.query.data.map((row) => getInitialValuesExistingRow(initializer.entity, row));
};

// Field Persistence Types included on Create
const USED_ON_CREATE = [PersistenceType.Normal, PersistenceType.FrontEndGenerated, PersistenceType.SetOnCreateOnly];
const USED_ON_UPDATE = [PersistenceType.Normal, PersistenceType.FrontEndGenerated];

const addValueToObject = (toObj: any, key: string, value: any) => {
  if (value) {
    toObj[key] = value;
  }
};

export const nullObjectIfEmpty = (obj: any) => (Object.keys(obj).filter((item) => !!item).length === 0 ? null : obj);

const isMulti = (initializer: Initializer<{}>): boolean =>
  ['create-multi', 'update-multi'].indexOf(initializer.type) > -1;

export const getUpdatingFieldsAndValues = <T extends Row<T>>(entity: Entity<T>, rowValues: FormRow<T>) =>
  getFieldArray(entity)
    .filter(
      ([field, _fieldId]) =>
        (rowValues._action === 'C' ? USED_ON_CREATE : USED_ON_UPDATE).indexOf(
          field.persistence || PersistenceType.Normal,
        ) > -1,
    )
    .map(([field, fieldId]): [typeof field, string, unknown] => [
      field,
      fieldId,
      (rowValues as Record<string, unknown>)[fieldId],
    ]);

export const validateAll = (init: Initialization, values: AllValues): any => {
  const errors: any = {};
  Object.keys(init).forEach((valuesKey) => {
    const initializer = init[valuesKey];
    if (isMulti(initializer)) {
      const gridErrors = (values[valuesKey] as FormRow<{}>[]).map((row) => {
        return validateRow(initializer.entity, row);
      });
      if (gridErrors.filter((error) => !!error).length > 0) {
        errors[valuesKey] = gridErrors;
      }
    } else {
      addValueToObject(errors, valuesKey, validateRow(initializer.entity, values[valuesKey] as FormRow<{}>));
    }
  });
  return nullObjectIfEmpty(errors);
};

export const validateRow = <T extends Row<T>>(
  entity: Entity<T>,
  rowValues: FormRow<T>,
): Record<string, string> | null => {
  const errors: Record<string, string> = {};
  if (rowValues._action === 'D' || rowValues._deleting) {
    return null; // skip deletes
  }
  getUpdatingFieldsAndValues(entity, rowValues).forEach(([field, fieldId, value]) => {
    addValueToObject(errors, fieldId, validate(field, value as any));
  });
  return nullObjectIfEmpty(errors);
};

export const makeCompoundPayload = (outcomeId: string) =>
  ({
    transId: getUuid(),
    outcomeId,
    frontEndSaveAt: new Date().toISOString(),
    frontEndPath: location.pathname,
    rows: [],
  }) as CompoundPayload;

export const addRowsToPayload = (init: Initialization, payload: CompoundPayload, values: AllValues): void => {
  Object.keys(init).forEach((valuesKey) => {
    const initializer = init[valuesKey];
    if (isMulti(initializer)) {
      (values[valuesKey] as FormRow<{}>[]).forEach((row) => {
        addRowToPayload(payload, initializer.entity, row);
      });
    } else {
      addRowToPayload(payload, initializer.entity, values[valuesKey] as FormRow<{}>);
    }
  });
};

export const addRowToPayload = <T extends Row<T>>(
  payload: CompoundPayload,
  entity: Entity<T>,
  rowValues: FormRow<T>,
): void => {
  const data: Partial<T> = {};
  const mutableData = data as Record<string, unknown>;
  if (rowValues._deleting) {
    if (rowValues._action === 'C') {
      return; // skip create-and-deletes
    }
    rowValues._action = 'D';
  }
  getUpdatingFieldsAndValues(entity, rowValues).forEach(([_field, fieldId, value]) => {
    mutableData[fieldId] = value;
  });
  const row: RowEnvelope<T> = {
    entityId: entity.id,
    action: rowValues._action,
    data: data as T,
  };
  row.key = rowValues._original_key;
  payload.rows.push(row);
};

export const notifyResponse = (resp: CompoundResponse, defaultSuccess?: string) => {
  if (resp.info) {
    notify([resp.info, 'success']);
  }
  if (resp.warn) {
    notify([resp.warn, 'warning']);
  }
  if (resp.error) {
    notify([resp.error, 'danger']);
  }
  if (!resp.info && !resp.warn && !resp.error) {
    notify([defaultSuccess || 'save successful', 'success']);
  }
};

export const notifyError = (error: any, defaultError?: string) => {
  console.error(defaultError || 'an error occurred', error);
  notify([error?.message || defaultError || 'an error occurred', 'danger']);
};
